// Shared logic for the UCC numeric range filters (annual revenue, employees,
// time in business, filing count): shorthand parsing, compact echo formatting,
// the snapped slider ladder, and preset-chip selection.

export type NumericRange = [number, number];

export type NumericRangeBounds = {
	min: number;
	max: number;
	step: number;
};

export type NumericRangeKind = "currency" | "count" | "years";

export type NumericRangePreset = {
	label: string;
	min: number;
	max: number;
};

const SUFFIX_MULTIPLIERS: Record<string, number> = {
	k: 1_000,
	m: 1_000_000,
	mm: 1_000_000,
	b: 1_000_000_000,
	bn: 1_000_000_000,
	t: 1_000_000_000_000,
};

export function clampRangeNumber(value: number, min: number, max: number) {
	return Math.min(max, Math.max(min, value));
}

/**
 * Parses the free-text Min/Max inputs. Accepts plain digits with or without
 * separators (`10,000,000`), a leading `$`, a trailing `+`, decimals, and the
 * k / m / b / t suffixes in any case (`5m`, `2.5M`, `500k`, `1b`, `$10M`).
 * Returns a rounded integer, or null when the text is empty or unreadable.
 */
export function parseNumericRangeInput(input: string): number | null {
	const normalized = input.replace(/[$,\s_]/g, "").replace(/\+$/, "").toLowerCase();
	if (!normalized) return null;
	const match = /^(\d*\.?\d+)(k|mm|m|bn|b|t)?$/.exec(normalized);
	if (!match) return null;
	const base = Number(match[1]);
	if (!Number.isFinite(base)) return null;
	const multiplier = match[2] ? SUFFIX_MULTIPLIERS[match[2]] : 1;
	return Math.round(base * multiplier);
}

function formatCompact(value: number) {
	return Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function formatFull(value: number) {
	return value.toLocaleString("en-US");
}

/** Short label used on tooltips, chips, and the summary: `$5M`, `1K`, `25`. */
export function formatNumericRangeCompact(value: number, kind: NumericRangeKind) {
	if (kind === "currency") return value === 0 ? "$0" : `$${formatCompact(value)}`;
	if (kind === "count") return value >= 10_000 ? formatCompact(value) : formatFull(value);
	return formatFull(value);
}

/**
 * The text echoed back into a Min/Max input after a value is applied. Uses the
 * compact form only when it round-trips exactly (so `$5M` but never `$1.2M`
 * for 1,234,567); otherwise the full number with separators.
 */
export function formatNumericRangeInput(value: number, kind: NumericRangeKind) {
	const compact = formatNumericRangeCompact(value, kind);
	if (parseNumericRangeInput(compact) === value) return compact;
	return kind === "currency" ? `$${formatFull(value)}` : formatFull(value);
}

/**
 * Live formatting for the Min/Max inputs while the user types, matching the
 * credits purchase page: the integer digits are grouped with commas as they
 * are entered (`5000000` -> `5,000,000`, `$12345.5m` -> `$12,345.5m`). A
 * leading `$`, a decimal part, and the k / m / b / t shorthand or trailing `+`
 * are left exactly as typed, so `parseNumericRangeInput` reads the result the
 * same way. Text without a leading integer part is returned unchanged.
 */
export function formatNumericRangeDraft(raw: string): string {
	const match = /^(\$?)(\d[\d,]*)(\.\d*)?(.*)$/s.exec(raw);
	if (!match) return raw;
	const [, currency, integerPart, decimalPart = "", rest] = match;
	const digits = integerPart.replace(/,/g, "");
	const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return `${currency}${grouped}${decimalPart}${rest}`;
}

/**
 * Where the caret belongs after `raw` (with the caret at `caret`) has been
 * re-rendered as `formatted`: the same number of non-comma characters stay to
 * the left of it, so inserted or removed separators never jump the cursor.
 */
export function formattedDraftCaret(raw: string, caret: number, formatted: string): number {
	const keepBefore = raw.slice(0, Math.max(0, caret)).replace(/,/g, "").length;
	let next = 0;
	let seen = 0;
	while (next < formatted.length && seen < keepBefore) {
		if (formatted[next] !== ",") seen += 1;
		next += 1;
	}
	return next;
}

export function normalizeNumericRange(range: NumericRange, bounds: NumericRangeBounds): NumericRange {
	const first = clampRangeNumber(Math.round(range[0]), bounds.min, bounds.max);
	const second = clampRangeNumber(Math.round(range[1]), bounds.min, bounds.max);
	return [Math.min(first, second), Math.max(first, second)];
}

export function isNumericRangeAtBounds(range: NumericRange, bounds: NumericRangeBounds) {
	return range[0] === bounds.min && range[1] === bounds.max;
}

/** Index of the ladder stop closest to `value` (ties resolve upward). */
export function nearestLadderIndex(value: number, ladder: readonly number[]) {
	if (ladder.length === 0) return 0;
	let bestIndex = 0;
	let bestDistance = Number.POSITIVE_INFINITY;
	ladder.forEach((stop, index) => {
		const distance = Math.abs(stop - value);
		if (distance < bestDistance || (distance === bestDistance && stop > value)) {
			bestDistance = distance;
			bestIndex = index;
		}
	});
	return bestIndex;
}

/**
 * Indexes of the preset chips that are currently "on". A chip is on only when
 * the active range is exactly the hull of a contiguous run of chips, so an
 * unfiltered range (full bounds) lights nothing even though it covers every chip.
 */
export function activePresetIndexes(range: NumericRange, presets: readonly NumericRangePreset[], bounds: NumericRangeBounds): number[] {
	if (presets.length === 0 || isNumericRangeAtBounds(range, bounds)) return [];
	const contained = presets.map((preset, index) => ({ preset, index })).filter(({ preset }) => range[0] <= preset.min && preset.max <= range[1]);
	if (contained.length === 0) return [];
	const first = contained[0];
	const last = contained[contained.length - 1];
	const contiguous = last.index - first.index === contained.length - 1;
	if (!contiguous) return [];
	if (first.preset.min !== range[0] || last.preset.max !== range[1]) return [];
	return contained.map(({ index }) => index);
}

/**
 * Applies a chip tap. Adding a chip extends the selection to the hull of the
 * old selection plus the new chip, so a gap between two taps fills in (the API
 * accepts one range per field). Removing an end chip shrinks the hull; removing
 * a middle chip keeps the longer side. An empty selection resets to the bounds.
 */
export function togglePresetSelection(range: NumericRange, presets: readonly NumericRangePreset[], index: number, bounds: NumericRangeBounds): NumericRange {
	if (index < 0 || index >= presets.length) return range;
	const active = activePresetIndexes(range, presets, bounds);
	let nextIndexes: number[];
	if (active.includes(index)) {
		const remaining = active.filter((item) => item !== index);
		if (remaining.length === 0) return [bounds.min, bounds.max];
		const left = remaining.filter((item) => item < index);
		const right = remaining.filter((item) => item > index);
		if (left.length > 0 && right.length > 0) {
			nextIndexes = left.length >= right.length ? left : right;
		} else {
			nextIndexes = remaining;
		}
	} else if (active.length === 0) {
		nextIndexes = [index];
	} else {
		const low = Math.min(index, ...active);
		const high = Math.max(index, ...active);
		nextIndexes = [];
		for (let item = low; item <= high; item += 1) nextIndexes.push(item);
	}
	const low = presets[nextIndexes[0]];
	const high = presets[nextIndexes[nextIndexes.length - 1]];
	return normalizeNumericRange([low.min, high.max], bounds);
}

// ---------------------------------------------------------------------------
// Per-filter configuration

export const annualRevenueBounds: NumericRangeBounds = { min: 0, max: 100_000_000_000, step: 50_000 };
export const annualRevenueLadder: readonly number[] = [
	0, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000, 10_000_000, 25_000_000, 50_000_000, 100_000_000, 250_000_000, 500_000_000, 1_000_000_000,
	2_500_000_000, 5_000_000_000, 10_000_000_000, 25_000_000_000, 50_000_000_000, 100_000_000_000,
];
export const annualRevenuePresets: readonly NumericRangePreset[] = [
	{ label: "Under $1M", min: 0, max: 1_000_000 },
	{ label: "$1M - $5M", min: 1_000_000, max: 5_000_000 },
	{ label: "$5M - $10M", min: 5_000_000, max: 10_000_000 },
	{ label: "$10M - $50M", min: 10_000_000, max: 50_000_000 },
	{ label: "$50M - $100M", min: 50_000_000, max: 100_000_000 },
	{ label: "$100M+", min: 100_000_000, max: annualRevenueBounds.max },
];

export const numberEmployeesBounds: NumericRangeBounds = { min: 0, max: 10_000, step: 1 };
export const numberEmployeesLadder: readonly number[] = [0, 1, 5, 10, 25, 50, 100, 200, 500, 1_000, 2_500, 5_000, 10_000];
export const numberEmployeesPresets: readonly NumericRangePreset[] = [
	{ label: "1 - 10", min: 1, max: 10 },
	{ label: "11 - 50", min: 11, max: 50 },
	{ label: "51 - 200", min: 51, max: 200 },
	{ label: "201 - 1,000", min: 201, max: 1_000 },
	{ label: "1,000+", min: 1_000, max: numberEmployeesBounds.max },
];

export const timeInBusinessBounds: NumericRangeBounds = { min: 0, max: 100, step: 1 };
export const timeInBusinessLadder: readonly number[] = [0, 1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 40, 50, 75, 100];
export const timeInBusinessPresets: readonly NumericRangePreset[] = [
	{ label: "Under 2 yrs", min: 0, max: 2 },
	{ label: "2 - 5 yrs", min: 2, max: 5 },
	{ label: "5 - 10 yrs", min: 5, max: 10 },
	{ label: "10 - 25 yrs", min: 10, max: 25 },
	{ label: "25+ yrs", min: 25, max: timeInBusinessBounds.max },
];

export const numberFilingsBounds: NumericRangeBounds = { min: 1, max: 1_000, step: 1 };
export const numberFilingsLadder: readonly number[] = [1, 2, 3, 5, 10, 15, 20, 25, 50, 75, 100, 250, 500, 1_000];
export const numberFilingsPresets: readonly NumericRangePreset[] = [
	{ label: "1 filing", min: 1, max: 1 },
	{ label: "2 - 5", min: 2, max: 5 },
	{ label: "6 - 10", min: 6, max: 10 },
	{ label: "11 - 50", min: 11, max: 50 },
	{ label: "50+", min: 50, max: numberFilingsBounds.max },
];
